// Asked over HTTP, not the socket: a refused socket upgrade looks the same as an
// unreachable server, so it cannot tell "sign in" from "denied" from "broken".
import {
  API_PATHS,
  AUTH_PATHS,
  actionDoneSchema,
  type Denial,
  denialSchema,
  type Session,
  sessionSchema,
} from '@alphadesk/web/contract.ts';
import { useCallback, useEffect, useState } from 'react';
import { ask } from './act.ts';
import { note, stopUsage } from './posthog.ts';

type SessionState =
  | { status: 'loading' }
  | { status: 'in'; session: Session }
  | { status: 'out' }
  | {
      status: 'denied';
      denial: Denial;
      name: string;
      email: string;
      picture: string | null;
      invitedBy: string | null;
      viewing: boolean;
    }
  | { status: 'error' };

export function useSession(signedOut: boolean): SessionState & { reask: () => void } {
  const [state, setState] = useState<SessionState>({ status: signedOut ? 'out' : 'loading' });
  const [asked, setAsked] = useState(0);
  const reask = useCallback(() => setAsked((n) => n + 1), []);

  // `asked` is a re-ask trigger, not an input.
  useEffect(() => {
    void asked;
    const abort = new AbortController();

    fetch(API_PATHS.me, { signal: abort.signal, credentials: 'same-origin' })
      .then(async (response) => {
        if (response.status === 401) return { status: 'out' } as const;
        if (response.status === 403) {
          const body = denialSchema.safeParse(await response.json());
          return body.success
            ? ({ status: 'denied', ...body.data } as const)
            : ({ status: 'error' } as const);
        }
        if (!response.ok) return { status: 'error' } as const;
        const body = sessionSchema.safeParse(await response.json());
        return body.success
          ? ({ status: 'in', session: body.data } as const)
          : ({ status: 'error' } as const);
      })
      .then((next) => setState(next))
      .catch((error: unknown) => {
        if (abort.signal.aborted) return;
        if (error instanceof Error && error.name === 'AbortError') return;
        setState({ status: 'error' });
      });

    return () => abort.abort();
  }, [asked]);

  return { ...state, reask };
}

export async function requestAccess(): Promise<
  { ok: true; said: string } | { ok: false; why: string }
> {
  const answer = await ask(API_PATHS.act, { do: 'request-access' }, actionDoneSchema);
  return answer.ok
    ? { ok: true, said: answer.data.said }
    : { ok: false, why: answer.violations.join(' ') };
}

export async function signOut(): Promise<void> {
  note('signed out');
  // Before navigating: the next person at this browser must not arrive identified.
  stopUsage();
  await fetch(AUTH_PATHS.logout, { method: 'POST', credentials: 'same-origin' });
  window.location.assign('/');
}

/** On success the page is gone: this browser is signed out and lands on the deletion said. */
export async function deleteAccount(
  confirm: string,
): Promise<{ ok: true } | { ok: false; violations: string[] }> {
  const answer = await ask(API_PATHS.account, { confirm }, actionDoneSchema, 'DELETE');
  if (!answer.ok) return answer;
  // Nothing about the deletion is reported; the next person here must not arrive identified.
  stopUsage();
  window.location.assign('/?signin=deleted');
  return { ok: true };
}
